import type { BinderWord } from '../stores/binderStore';

// Merriam-Webster paste box (2026-10-25, Odin's spec). Turns MW's
// pronunciation symbols into our house respelling by fixed rules only: no
// model, no fetch. Odin pastes MW's text by hand and still clicks Approve.
//
// Rules: hyphens are syllable breaks; ˈ = primary stress (CAPITALS), ˌ =
// secondary (lowercase); ə is "uh" at the end of its syllable, "u" before a
// consonant in it, and "u" when stressed; ē ee, ā ay, ī eye (a syllable ending
// ī + one consonant reads i_e: LUD-ite), ō oh, ü oo, u̇ oo, ä ah, ȯ aw, ȯi oy,
// au̇ ow, ər ur, ŋ ng, t͟h th; plain letters stay. A stressed syllable ending in
// a short vowel takes the first consonant of the next syllable (TRUK-yuh-lunt).
// Only the first pronunciation is used. MW parentheses mark optional sounds
// (Odin, 2026-10-25): an optional extra syllable that starts with a hyphen,
// fī(-ə)l, is dropped; an optional sound inside a syllable, ˈpräm(p)t, is kept
// without its brackets. Any other parentheses or an unmapped symbol means no
// conversion.

type Phoneme = { text: string; vowel: boolean; short?: boolean; schwa?: boolean; long_i?: boolean };
type Syllable = { stress: 0 | 1 | 2; ph: Phoneme[]; sep: string };

const PHONEMES: [string, Phoneme][] = [
  ['t͟h', { text: 'th', vowel: false }],
  ['au̇', { text: 'ow', vowel: true }],
  ['ȯi', { text: 'oy', vowel: true }],
  ['ər', { text: 'ur', vowel: true }],
  ['u̇', { text: 'oo', vowel: true, short: true }],
  ['ch', { text: 'ch', vowel: false }],
  ['sh', { text: 'sh', vowel: false }],
  ['th', { text: 'th', vowel: false }],
  ['zh', { text: 'zh', vowel: false }],
  ['ŋ', { text: 'ng', vowel: false }],
  ['ē', { text: 'ee', vowel: true }],
  ['ā', { text: 'ay', vowel: true }],
  ['ī', { text: 'eye', vowel: true, long_i: true }],
  ['ō', { text: 'oh', vowel: true }],
  ['ü', { text: 'oo', vowel: true }],
  ['ä', { text: 'ah', vowel: true }],
  ['ȯ', { text: 'aw', vowel: true }],
  ['ə', { text: 'uh', vowel: true, short: true, schwa: true }],
  // Raised ə (button ˈbə-tᵊn) reads exactly like ə (Odin, 2026-10-25).
  ['ᵊ', { text: 'uh', vowel: true, short: true, schwa: true }],
  ...'aeiou'.split('').map((c): [string, Phoneme] => [c, { text: c, vowel: true, short: true }]),
  ...'bcdfghjklmnpqrstvwxyz'.split('').map((c): [string, Phoneme] => [c, { text: c, vowel: false }]),
];

const PRIMARY = 'ˈ';
const SECONDARY = 'ˌ';

/** The respelling for a pasted MW pronunciation, or null when it can't be converted by the rules. */
export function mwToRespelling(pasted: string): string | null {
  const first = pasted.normalize('NFC').split(/[,;]/)[0].replace(/\\/g, '').trim().toLowerCase()
    .replace(/\(-[^()\-\s]+\)/g, '')
    .replace(/\(([^()\-\s\u02C8\u02CC]+)\)/g, '$1');
  if (!first || /[()]/.test(first)) return null;

  const syllables: Syllable[] = [];
  let cur: Syllable = { stress: 0, ph: [], sep: '' };
  const close = (sep: string) => {
    if (!cur.ph.length) return false;
    cur.sep = sep;
    syllables.push(cur);
    cur = { stress: 0, ph: [], sep: '' };
    return true;
  };
  let i = 0;
  while (i < first.length) {
    const ch = first[i];
    if (ch === '-' || ch === ' ') {
      if (!close(ch)) return null;
      i++;
      while (first[i] === ' ') i++;
      continue;
    }
    if (ch === PRIMARY || ch === SECONDARY) {
      // A stress mark inside a syllable starts a new one.
      if (cur.ph.length) close('-');
      if (cur.stress) return null;
      cur.stress = ch === PRIMARY ? 1 : 2;
      i++;
      continue;
    }
    const hit = PHONEMES.find(([sym]) => first.startsWith(sym, i));
    if (!hit) return null;
    cur.ph.push(hit[1]);
    i += hit[0].length;
  }
  if (!close('')) return null;

  // A stressed syllable ending in a short vowel takes the next syllable's first consonant.
  syllables.forEach((s, n) => {
    const next = syllables[n + 1];
    const last = s.ph[s.ph.length - 1];
    if (!s.stress || !last.vowel || !last.short || !next || s.sep !== '-') return;
    if (next.ph.length > 1 && !next.ph[0].vowel && next.ph.some((p) => p.vowel)) s.ph.push(next.ph.shift()!);
  });

  return syllables.map((s) => {
    let out = '';
    s.ph.forEach((p, j) => {
      const after = s.ph.slice(j + 1);
      if (p.schwa) out += s.stress || after.some((q) => !q.vowel) ? 'u' : 'uh';
      else if (p.long_i && after.length === 1 && !after[0].vowel) out += 'i';
      else out += p.text;
    });
    const l = s.ph.length;
    if (l >= 2 && s.ph[l - 2].long_i && !s.ph[l - 1].vowel) out += 'e';
    return (s.stress === 1 ? out.toUpperCase() : out) + s.sep;
  }).join('');
}

/**
 * The next word needing a pronunciation check after `currentId`: Game words
 * first, earliest scheduled game date first (undated last), then A to Z.
 */
export function nextPronunciationCheck(game: BinderWord[], social: BinderWord[], currentId: string): BinderWord | null {
  const due = (w: BinderWord) => w.id !== currentId && !!w.pronunciation?.needs_check;
  const order = (a: BinderWord, b: BinderWord) =>
    (a.game_date || '9999').localeCompare(b.game_date || '9999') || a.word.localeCompare(b.word);
  return game.filter(due).sort(order)[0] || social.filter(due).sort(order)[0] || null;
}
